<template>
    <div class="bedrock-chat-widget" :class="'bedrock-chat-widget-' + bubbleSide">
        <button v-if="collapsed" class="bedrock-chat-bubble" :class="{ dragging: bubbleDragging }" :style="{ transform: bubbleTransform }" @pointerdown="startBubbleDrag" @click="handleBubbleClick" aria-label="Open K-AI chat." title="Open K-AI chat.">
            <span class="chat-bubble-icon">
                <i class="fas fa-robot"></i>
            </span>
        </button>

        <div v-else class="bedrock-chat-panel">
            <div class="bedrock-chat-header">
                <div class="bedrock-chat-title">K-AI (Beta)</div>
                <div class="bedrock-chat-actions">
                    <button type="button" @click="resetChat" title="New chat">Clear</button>
                    <button type="button" @click="closeChat" title="Minimize chat">-</button>
                </div>
            </div>

            <div class="bedrock-chat-messages" ref="messagesContainer">
                <div v-for="(message, index) in messages" :key="'message-' +index" class="bedrock-chat-message" :class="message.role">
                    {{ message.text }}
                </div>
                <div v-if="loading" class="bedrock-chat-message assistant">
                    <span class="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                    </span>
                </div>
            </div>

            <form class="bedrock-chat-form" @submit.prevent="sendMessage">
                <input v-model="draftMessage" type="text" class="bedrock-chat-input" placeholder="Tell me how I can help." :disabled="loading" > <button type="submit" :disabled="loading || !draftMessage.trim()"> Send </button>
            </form>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BedrockChatWidget',
    data() {
        return {
            collapsed: true,
            messages: [
                {
                    role: 'assistant',
                    text: "Hello! Ask me anything you want about FlightDeck. I'm here to help!"
                }
            ],
            draftMessage: '',
            loading: false, 
            sessionId: null, 
            bedrockToken: '', 
            bedrockTokenExpiresAt: null, 
            bubbleDragging: false,
            bubbleSide: window.localStorage.getItem('bedrock_chat_bubble_side') || 'right',
            bubbleDragStartX: 0, 
            bubbleDragOffsetX: 0,
            bubbleMoved: false
        }
    },
    created() {
        this.initializeSession(); 
        this.loadSavedMessages();
        this.saveMessages();
    }, 
    beforeDestroy() {
        window.removeEventListener('pointermove', this.moveBubbleDrag);
        window.removeEventListener('pointerup', this.endBubbleDrag);
    },
    computed: {
        resolvedHelpcenterApiUrl() {
           if (this.helpcenterApiUrl) {
            return this.helpcenterApiUrl.replace(/\/+$/, '');
           }

            return '';
        },
        bubbleTransform() {
            if (!this.bubbleDragging) {
                return '';
            }

            return 'translateX(' + this.bubbleDragOffsetX + 'px)';
        }
    },
    props: {
        fdToken: {
            type: String,
            default: ''
        },
        appVersion: {
            type: String,
            default: ''
        }, 
        helpcenterApiUrl :{
            type: String, 
            default: ''
        }
    },
    methods: {
        initializeSession() {
            let existingSessionId = window.localStorage.getItem('bedrock_chat_session_id');

            if( !existingSessionId) {
                if (window.crypto && window.crypto.randomUUID) {
                    existingSessionId = window.crypto.randomUUID();
                } else {
                    existingSessionId = 'session-' + Date.now() + '-' + Math.random().toString(36).substring(2, 10);
                }

                window.localStorage.setItem('bedrock_chat_session_id', existingSessionId);
            }
            this.sessionId = existingSessionId; 
        },
        loadSavedMessages() {
            const savedMessages = window.sessionStorage.getItem('bedrock_chat_messages');

            if (savedMessages) {
                try {
                    this.messages = JSON.parse(savedMessages);
                } catch (error) {
                   this.messages = [
                    {
                        role: 'assistant',
                        text: "Hello! Ask me anything you want about FlightDeck. I'm here to help!"
                    }
                   ];
                }
            }
        }, 
        startBubbleDrag(event) {
            this.bubbleDragging = true;
            this.bubbleDragStartX = event.clientX;
            this.bubbleMoved = false;

            window.addEventListener('pointermove', this.moveBubbleDrag);
            window.addEventListener('pointerup', this.endBubbleDrag);
        },
        moveBubbleDrag(event) {
            if (!this.bubbleDragging) {
                return;
            }

            const offsetX = event.clientX - this.bubbleDragStartX;

            if (Math.abs(offsetX) > 5) {
                this.bubbleMoved = true;
            }

            this.bubbleDragOffsetX = offsetX;
        },
        endBubbleDrag(event) {
            if (!this.bubbleMoved) {
                return;
            }

            if (this.bubbleMoved) {
                const screenMiddle = window.innerWidth / 2;

                this.bubbleSide = event.clientX < screenMiddle ? 'left' : 'right'; 
                window.localStorage.setItem('bedrock_chat_bubble_side', this.bubbleSide);
            }

            this.bubbleDragging = false;
            this.bubbleDragOffsetX = 0;

            window.removeEventListener('pointermove', this.moveBubbleDrag);
            window.removeEventListener('pointerup', this.endBubbleDrag);
        },
        handleBubbleClick() {
            if (this.bubbleMoved) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            this.openChat();
        },
        openChat() {
            this.collapsed = false;
            this.scrollToBottom();
        }, 
        closeChat() {
            this.collapsed = true;
        }, 
        resetChat() {
            this.messages = [
                {
                    role: 'assistant',
                    text: "Hello! Ask me anything you want about FlightDeck. I'm here to help!"
                }
            ];
            this.draftMessage = '';

            if (window.crypto && window.crypto.randomUUID) {
                this.sessionId = window.crypto.randomUUID();
            } else {
                this.sessionId = 'session-' + Date.now() + '-' + Math.random().toString(36).substring(2, 10);
            }

            window.localStorage.setItem('bedrock_chat_session_id', this.sessionId);
            this.saveMessages();
            this.scrollToBottom();
        }, 
         async sendMessage() {
            const text = this.draftMessage.trim();

            if (!text || this.loading) {
                return;
            }

            this.messages.push({
                role: 'user',
                text: text
            });

            this.draftMessage = '';
            this.loading = true;
            this.saveMessages();
            this.scrollToBottom();

            try {
                const token = await this.getBedrockToken();

                const response = await fetch(`${this.resolvedHelpcenterApiUrl}/chat`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        sessionId: this.sessionId,
                        message: text
                    })
                });

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(result.message || "Unable to get a response from Bedrock.");
                }
                
                if(result.sessionId) {
                    this.sessionId = result.sessionId;
                    window.localStorage.setItem('bedrock_chat_session_id', this.sessionId);
                }

                this.messages.push({
                    role: 'assistant',
                    text: result.reply || 'No response returned'
                });
            } catch (error) {
                this.messages.push({
                    role: 'assistant',
                    text: 'Sorry, there was an error processing your request. Please try again.'
                });
            } finally {
                this.loading = false;
                this.saveMessages();
                this.scrollToBottom();
            }
        }, 
        saveMessages() {
            window.sessionStorage.setItem('bedrock_chat_messages', JSON.stringify(this.messages));
        }, 
        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.messagesContainer;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        }, 
        async getBedrockToken() {
            if (this.bedrockToken && this.bedrockTokenExpiresAt && Date.now() < this.bedrockTokenExpiresAt) {
                return this.bedrockToken;
            }

            if (!this.fdToken) {
                throw new Error('Missing Flightdeck token.');
            }

            const response = await fetch(`${this.resolvedHelpcenterApiUrl}/auth/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    fd_token: this.fdToken,
                    app_version: this.appVersion,
                    hostname: window.location.hostname,
                    source: 'DASHBOARD',
                    app_route: this.$route ? this.$route.path : window.location.pathname
                })
            });

            const data = await response.json();

            if (!response.ok || !data.success || !data.token) {
                throw new Error(data.error || 'Unable to issue Bedrock token.');
            }

            this.bedrockToken = data.token;
            this.bedrockTokenExpiresAt = Date.now() + ((data.expiresIn || 3600) - 60) * 1000;

            return this.bedrockToken;
        },
    }
}
</script>

<style scoped>
.bedrock-chat-widget {
    position: fixed;
    bottom: 24px;
    z-index: 2147483647;
}

.bedrock-chat-widget-right {
    right: 24px;
    left: auto;
}

.bedrock-chat-widget-left {
    left: 24px;
    right: auto;
}

.bedrock-chat-bubble {
    width: 68px;
    height: 68px;
    border: none;
    border-radius: 999px;
    background: linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%);
    color: white;
    cursor: grab;
    touch-action: none;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.28);
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    padding: 0;
    font-size: 0;
}

.bedrock-chat-bubble:hover {
    box-shadow: 0 18px 36px rgba(15, 23, 42, 0.34);
}

.bedrock-chat-panel {
    width: 360px;
    height: 560px;
    background: #ffffff;
    border-radius: 20px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
    border: 1px solid rgba(15, 23, 42, 0.08);
}

.bedrock-chat-header {
    background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%);
    color: white;
    padding: 14px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.bedrock-chat-messages {
    flex: 1;
    padding: 18px 14px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: #f8fafc;
}

.bedrock-chat-message {
    max-width: 82%;
    padding: 11px 13px;
    border-radius: 16px;
    line-height: 1.45;
    font-size: 14px;
    white-space: pre-wrap;
    word-break: break-word;
}

.bedrock-chat-message.user {
    align-self: flex-end;
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    color: white;
    border-bottom-right-radius: 6px;
}

.bedrock-chat-message.assistant {
    align-self: flex-start;
    background: white;
    color: #0f172a;
    border: 1px solid #e2e8f0;
    border-bottom-left-radius: 6px;
}

.bedrock-chat-form {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 14px;
    border-top: 1px solid #e2e8f0;
    background: white;
}

.bedrock-chat-input {
    flex: 1;
    min-width: 0;
    border: 1px solid #cbd5e1;
    border-radius: 14px;
    padding: 12px 14px;
    font-size: 14px;
    outline: none;
}

.bedrock-chat-input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.bedrock-chat-form button {
    border: none;
    border-radius: 14px;
    background: #2563eb;
    color: white;
    padding: 12px 16px;
    font-weight: 600;
    cursor: pointer;
    margin-left: 0;
}

.bedrock-chat-actions {
    display: flex;
    gap: 8px;
}

.bedrock-chat-actions button {
    border: none;
    background: rgba(255, 255, 255, 0.14);
    color: white;
    border-radius: 10px;
    padding: 7px 10px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
}

.typing-indicator span {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #94a3b8;
    display: inline-block;
    animation: typing-bounce 1.2s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.15s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.3s;
}

@keyframes typing-bounce {
    0%, 80%, 100% {
        transform: scale(0.7);
        opacity: 0.55;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

.chat-bubble-icon {
    width: 42px;
    height: 42px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.chat-bubble-icon i {
    font-size: 18px;
    color: white;
}

.bedrock-chat-bubble.dragging {
    cursor: grabbing;
}

</style>